import { pickGenderText } from "./genderText.js";

function isGenderNode(node) {
  return (
    !!node &&
    typeof node === "object" &&
    !Array.isArray(node) &&
    ("male" in node || "female" in node || "other" in node)
  );
}

export function applyLegacyHebrewGender(text, gender = "other") {
  if (typeof text !== "string" || !text.includes("/")) return text;
  const g = String(gender || "other").toLowerCase();
  if (g !== "male" && g !== "female") return text;

  const suffix = g === "female" ? "$1ת" : "$1";
  const suffixHe = g === "female" ? "$1ה" : "$1";

  return text
    .replace(/([א-ת"'׳״]+)\/ת(?![א-ת])/g, suffix)
    .replace(/([א-ת"'׳״]+)\/ה(?![א-ת])/g, suffixHe);
}

const FEMALE_WORD_MAP = {
  "שואל": "שואלת",
  "כותב": "כותבת",
  "עוצר": "עוצרת",
  "יושב": "יושבת",
  "נלחם": "נלחמת",
  "נותן": "נותנת",
  "מרגיש": "מרגישה",
  "קורא": "קוראת",
  "מבין": "מבינה",
  "בוחר": "בוחרת",
  "אומר": "אומרת",
  "מדבר": "מדברת",
  "רוצה": "רוצה",
  "יכול": "יכולה",
  "נשאר": "נשארת",
  "עוזב": "עוזבת",
  "נעלם": "נעלמת",
  "דוחף": "דוחפת",
  "מטפל": "מטפלת",
  "מתגעגע": "מתגעגעת",
  "מבטיח": "מבטיחה",
  "משחרר": "משחררת",
  "מוכן": "מוכנה",
  "לוקח": "לוקחת",
  "בודק": "בודקת",
  "מחזיק": "מחזיקה",
  "מנסה": "מנסה",
  "מאמץ": "מאמצת",
  "מתחיל": "מתחילה",
  "מסתדר": "מסתדרת",
  "מבקש": "מבקשת",
  "ממשיך": "ממשיכה",
  "מכבד": "מכבדת",
  "שורד": "שורדת",
  "מפסיק": "מפסיקה",
  "אוהב": "אוהבת",
  "נמצא": "נמצאת",
  "בונה": "בונה",
  "מחליט": "מחליטה",
  "לומד": "לומדת",
  "חוזר": "חוזרת",
  "מפרק": "מפרקת",
  "מגן": "מגינה",
  "נוגע": "נוגעת",
  "מדמיין": "מדמיינת",
  "מצמצם": "מצמצמת",
  "מניח": "מניחה",
  "עובר": "עוברת",
  "נופל": "נופלת",
  "נפרד": "נפרדת",
  "מסיים": "מסיימת",
  "אמיץ": "אמיצה",
  "חזק": "חזקה",
};

function fixLegacyTypos(text) {
  if (typeof text !== "string") return text;

  return text
    .replace(/אני לא מאני בודק(?:\/ת|ת)? בעדינות/g, "אני לא מנסה")
    .replace(/ומה אני מאני בוחר(?:\/ת|ת)? להרגיע/g, "ומה אני מנסה להרגיע")
    .replace(/מה אני באמת מאני בודק(?:\/ת|ת)? בעדינות להרגיע/g, "מה אני באמת מנסה להרגיע")
    .replace(/מה הלופ הזה מאני בודק(?:\/ת|ת)? בעדינות להשיג בשבילי/g, "מה הלופ הזה מנסה להשיג בשבילי");
}

function applyFemaleHebrew(text) {
  if (typeof text !== "string") return text;
  let result = text;
  Object.entries(FEMALE_WORD_MAP).forEach(([male, female]) => {
    const pattern = new RegExp(
      `((?:^|[^א-ת])(?:אני|שאני|ואני|כשאני|כאילו אני|אם הייתי|מה הייתי|הייתי)(?:\\s+(?:לא|רק|עוד|באמת|עדיין|פשוט))?\\s+[“"']?)${male}(?=[^א-ת]|$)`,
      "g"
    );
    result = result.replace(pattern, `$1${female}`);
  });
  result = result.replace(/(להיות\s+)אמיץ(?=[^א-ת]|$)/g, "$1אמיצה");
  result = result.replace(/(שאני\s+)חזק(?=[^א-ת]|$)/g, "$1חזקה");
  return result;
}

function normalizeSeparators(text) {
  if (typeof text !== "string" || (!text.includes("/") && !text.includes("\\"))) {
    return text;
  }

  return text
    .replace(/\s*[\\/]\s*/g, ", ")
    .replace(/,\s*,+/g, ", ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

export function normalizeUserFacingText(text, { lang = "he", gender = "other" } = {}) {
  if (typeof text !== "string") return text;
  const fixed = lang === "he" ? fixLegacyTypos(text) : text;
  const slashed = lang === "he" ? applyLegacyHebrewGender(fixed, gender) : fixed;
  const gendered = lang === "he" && String(gender || "").toLowerCase() === "female" ? applyFemaleHebrew(slashed) : slashed;
  return normalizeSeparators(gendered);
}

function resolveLocalizedNode(node, requestedLang, gender, fallback, activeLang = requestedLang) {
  if (typeof node === "string") {
    return normalizeUserFacingText(node, { lang: activeLang, gender });
  }

  if (isGenderNode(node)) {
    return resolveLocalizedNode(pickGenderText(node, gender), requestedLang, gender, fallback, activeLang);
  }

  if (!node || typeof node !== "object") {
    return fallback;
  }

  if (node[requestedLang] != null) {
    return resolveLocalizedNode(node[requestedLang], requestedLang, gender, fallback, requestedLang);
  }

  if (node.he != null) {
    return resolveLocalizedNode(node.he, requestedLang, gender, fallback, "he");
  }

  if (node.en != null) {
    return resolveLocalizedNode(node.en, requestedLang, gender, fallback, "en");
  }

  if (node.es != null) {
    return resolveLocalizedNode(node.es, requestedLang, gender, fallback, "es");
  }

  return fallback;
}

export function resolveUserFacingText(node, { lang = "he", gender = "other", fallback = "" } = {}) {
  return resolveLocalizedNode(node, lang, gender, fallback);
}
